import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import Alert from '@/Components/Alert';
import { PlusIcon, PencilSquareIcon, TrashIcon, MapPinIcon, MagnifyingGlassIcon, FunnelIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

export default function Index({ locations, filters, flash }) {
    const [search, setSearch] = useState(filters?.search || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/work-locations', { search }, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        router.get('/work-locations');
    };

    const deleteLocation = (id) => {
        router.delete(`/work-locations/${id}`, {
            onSuccess: () => setDeleteConfirm(null),
        });
    };

    return (
        <AppLayout>
            <Head title="Work Locations" />
            <PageHeader
                title="Work Locations"
                subtitle="Manage work sites where employees are assigned"
                actions={
                    <Button href="/work-locations/create" size="sm">
                        <PlusIcon className="w-4 h-4" /> Add Location
                    </Button>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="error" className="mb-4">{flash.error}</Alert>}

            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search locations..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm"><FunnelIcon className="w-4 h-4" /> Apply</Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>Reset</Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card>
                <Table minWidth="600px">
                    <thead>
                        <tr>
                            <Th>#</Th>
                            <Th>Location Name</Th>
                            <Th>Departments</Th>
                            <Th>Description</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {locations?.map?.((location, idx) => (
                            <Tr key={location.id}>
                                <Td className="text-slate-400 text-xs">{idx + 1}</Td>
                                <Td>
                                    <div className="flex items-center gap-2">
                                        <MapPinIcon className="w-4 h-4 text-emerald-700" />
                                        <span className="font-medium text-slate-800">{location.name}</span>
                                    </div>
                                </Td>
                                <Td>
                                    {location.departments && location.departments.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {location.departments.map(dept => (
                                                <span
                                                    key={dept.id}
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        dept.is_active
                                                            ? 'bg-emerald-50 text-emerald-700'
                                                            : 'bg-slate-100 text-slate-400 line-through'
                                                    }`}
                                                >
                                                    <BuildingOfficeIcon className="w-3 h-3" />
                                                    {dept.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-slate-400 text-xs">—</span>
                                    )}
                                </Td>
                                <Td className="text-slate-500 text-sm">{location.description || '—'}</Td>
                                <Td>
                                    <Badge variant={location.is_active ? 'success' : 'neutral'}>
                                        {location.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </Td>
                                <Td>
                                    <div className="flex flex-wrap items-center gap-1">
                                        <Link
                                            href={`/work-locations/${location.id}/edit`}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition"
                                        >
                                            <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                                        </Link>
                                        <button
                                            onClick={() => setDeleteConfirm(location.id)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition"
                                        >
                                            <TrashIcon className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!locations || locations.length === 0) && (
                    <EmptyState
                        icon={MapPinIcon}
                        title="No work locations"
                        description="Create your first work location to get started."
                        action={
                            <Button href="/work-locations/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> Add Location
                            </Button>
                        }
                    />
                )}
            </Card>

            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Location"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">Are you sure you want to delete this work location? This action cannot be undone.</p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => deleteLocation(deleteConfirm)}>Delete</Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
